// Suggested Clinical Terms on a record about a pregnancy (TGCLOUD-1868, decision 19).
//
// A Test or a Clinical Note opened for a fetus is the MOTHER's record, scoped to the pregnancy and
// perhaps one fetus (prenatalScope.carriesPrenatalScope). A genotype read from its document is the
// fetus's or the pregnancy's, never hers, and the server writes none from such a record
// (decision 17: POST /clinical-notes/accept-suggestions skips each as `prenatal-source`). Offering
// one to tick, only for the accept to drop it, is a suggestion the clinician cannot take. So none is
// offered: the genotype suggestions are taken out of what the panel shows and what an accept can
// send, for this person and for every relative alike (the server's rule is the record's, whoever the
// genotype is for), and the panel says why. Diagnoses and phenotypes are offered as they always were.
//
// Pure: the hook (useClinicalSuggestions) applies it, the panel (ClinicalSuggestionsPanel) says it.

export const GENOTYPES_NOT_OFFERED_TEXT =
  'Genotype suggestions are not offered for a record about a pregnancy.';

// A DOCUMENT THAT GIVES RESULTS FOR OTHERS (TGCLOUD-1868 review, hazards 1868-H2 and H3): a trio's
// report, or a report about a pregnancy, on ANY record. The server offers no genotype from it
// (POST /clinical-notes/analyze-note: `genotypesNotOffered`, reason 'report-people'), since a row it
// prints may be the fetus's or a parent's, and the panel says so, naming the files.
export const REPORT_PEOPLE_GENOTYPES_NOT_OFFERED_TEXT =
  'Genotype suggestions are not offered from a report that gives results for more than one person, or for a pregnancy: a variant in it may be someone else’s.';

/**
 * The analysis with every genotype suggestion taken out, and how many were (`genotypesWithheld`).
 * A subject with none keeps its own object; one with some keeps every other finding as it was.
 *
 * @param {{ subjects?: Array<{ findings?: object[] }> }} analysis useClinicalSuggestions' merged
 *   analysis
 * @returns {object} the analysis, its subjects without genotype findings, and `genotypesWithheld`
 */
export function withoutGenotypeSuggestions(analysis) {
  const subjects = Array.isArray(analysis?.subjects) ? analysis.subjects : [];
  let withheld = 0;
  const kept = subjects.map((subject) => {
    const findings = Array.isArray(subject?.findings) ? subject.findings : [];
    const others = findings.filter((f) => f?.kind !== 'genotype');
    withheld += findings.length - others.length;
    return others.length === findings.length
      ? subject
      : { ...subject, findings: others };
  });
  return { ...analysis, subjects: kept, genotypesWithheld: withheld };
}
